// Pure helpers for the Swim Locations page, so the arithmetic is tested
// apart from the rendering. The numbers come from the backend's
// getSwimLocationStats; nothing here recounts.

import { EMPTY, toDate } from '../Shared/detailFormat'

// How a published location got its name, best first. Each bucket's color
// lives in index.css under its className: categorical slots 1-5 of the
// dataviz reference palette, in fixed order (validated for adjacent-pair
// CVD separation). Three sit below 3:1 on white, so the bar always renders
// beside a table of the same counts.
export const NAMING_BUCKETS = [
  {
    key: 'venue',
    label: 'Venue name',
    className: 'swim-locations-naming-venue',
    help: 'Named after an OpenStreetMap venue (a pool, beach or lake)'
  },
  {
    key: 'address',
    label: 'Address name',
    className: 'swim-locations-naming-address',
    help: 'Named from a street address: often a hotel, club or business'
  },
  {
    key: 'otherSource',
    label: 'Other source',
    className: 'swim-locations-naming-otherSource',
    help: 'Named by a source other than the current geocoder'
  },
  {
    key: 'unnamedGeocoded',
    label: 'No name found',
    className: 'swim-locations-naming-unnamedGeocoded',
    help: 'The current geocoder looked and found no venue to name it after'
  },
  {
    key: 'unnamedPending',
    label: 'Waiting for geocoder',
    className: 'swim-locations-naming-unnamedPending',
    help: 'No name yet, and the current geocoder has not reached it'
  }
]

// A share as a fraction, or null when there is no whole to divide by,
// so "0 of 0" renders as a dash rather than 0% or NaN.
export function share (part, whole) {
  if (!whole) {
    return null
  }
  return part / whole
}

export function formatPercent (fraction) {
  if (fraction === null || fraction === undefined) {
    return EMPTY
  }
  const percent = fraction * 100
  if (percent > 0 && percent < 1) {
    return '<1%'
  }
  if (percent < 100 && percent > 99) {
    return '>99%'
  }
  return `${Math.round(percent)}%`
}

export function formatCount (n) {
  return (n || 0).toLocaleString('en-US')
}

export function namedCount (kind) {
  const { venue, address, otherSource } = kind.naming
  return venue + address + otherSource
}

// Published rows with no name, whether or not the geocoder has looked.
export function unnamedCount (kind) {
  const { unnamedGeocoded, unnamedPending } = kind.naming
  return unnamedGeocoded + unnamedPending
}

// The naming bar's segments, in bucket order, empty buckets kept so the
// legend never shifts between pools and open water.
export function namingSegments (kind) {
  return NAMING_BUCKETS.map(bucket => ({
    ...bucket,
    count: kind.naming[bucket.key] || 0,
    share: share(kind.naming[bucket.key] || 0, kind.published)
  }))
}

// Enrichment coverage over published rows. Open water has no pool length,
// so that row is dropped rather than shown as a permanent 0%.
export function coverageRows (kind, isOpenWater) {
  const rows = [
    { key: 'name', label: 'Named', count: namedCount(kind) },
    { key: 'country', label: 'Country', count: kind.withCountry },
    { key: 'city', label: 'City', count: kind.withCity },
    { key: 'length', label: 'Pool length', count: kind.withPrimaryLength },
    {
      key: 'community',
      label: 'Community answers',
      count: kind.withCommunity
    },
    {
      key: 'waterTemperature',
      label: 'Water temperature',
      count: kind.withWaterTemperature
    }
  ]
  return rows
    .filter(row => !(isOpenWater && row.key === 'length'))
    .map(row => ({ ...row, share: share(row.count, kind.published) }))
}

const YARD_POOLS = {
  18.29: '20 yd',
  22.86: '25 yd',
  30.48: '33⅓ yd',
  45.72: '50 yd'
}

export function lengthLabel (lengthInMeters) {
  if (lengthInMeters === null || lengthInMeters === undefined) {
    return 'No length'
  }
  return YARD_POOLS[lengthInMeters] || `${lengthInMeters} m`
}

// "Kirkland, WA" from the geocoder when it has one, else the GeoNames
// city and country, else the coordinates.
export function locationLabel (row) {
  if (row.subtitle) {
    return row.subtitle
  }
  const place = [row.cityName, row.countryCode].filter(Boolean).join(', ')
  if (place) {
    return place
  }
  if (typeof row.latitude === 'number' && typeof row.longitude === 'number') {
    return `${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`
  }
  return EMPTY
}

export function osmUrl (row) {
  if (typeof row.latitude !== 'number' || typeof row.longitude !== 'number') {
    return null
  }
  const { latitude: lat, longitude: lng } = row
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}` +
    `#map=17/${lat}/${lng}`
}

// Days since a date, for "last geocoded 2 days ago". null for no date.
export function daysSince (date, now) {
  const then = toDate(date)
  if (!then) {
    return null
  }
  return Math.floor((now.getTime() - then.getTime()) / (24 * 60 * 60 * 1000))
}
